'use strict';

const sheetStyles = require('./sheet.styles.scss').default;
const webComponentsUtils = require('../../util/webComponentUtils');

/* eslint no-unused-expressions: ["error", { "allowShortCircuit": true }] */
class SheetComponent extends HTMLElement {
    static init() {
        if (!window.customElements || window.customElements.get('sheet-component')) {
            return;
        }

        window.customElements.define('sheet-component', SheetComponent);
    }

    static get observedAttributes() {
        return ['class'];
    }

    constructor() {
        super();
        this.transitionDuration = 300;

        this.attachShadow({ mode: 'open' });

        const template = document.createElement('template');
        template.innerHTML = `
        <style>${sheetStyles}</style>

        <div class='sheet-container' part='sheet-container' tabindex='0' role='dialog'>
            <div class='sheet-header' part='sheet-header'>
                <button class='sheet-handle' type='button' part='sheet-handle'>
                    <span part='sheet-handle-icon'></span>
                </button>
                <div class='sheet-header-text' part='sheet-header-text'>
                    <slot name='sheet-header-text'></slot>
                    <button type='button' class='sheet-close' part='sheet-close' aria-label='Close dialog'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                            <rect width="28" height="28" rx="4" fill="#EBECED"/>
                            <path d="M9.33333 18.6667L18.6667 9.33333M9.33333 9.33333L18.6667 18.6667" stroke="#151415" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div class='sheet-body' part='sheet-body'>
                <slot></slot>
            </div>
            <div class='sheet-footer' part='sheet-footer'>
                <slot name='sheet-footer' class='sheet-footer-slot'></slot>
            </div>
        </div>
        <div class='sheet-backdrop' part='sheet-backdrop'></div>

        `;

        this.shadowRoot.appendChild(template.content.cloneNode(true));

        this.sheetContainer = this.shadowRoot.querySelector('.sheet-container');
        this.sheetHandle = this.shadowRoot.querySelector('.sheet-handle');
        this.sheetCloseButton = this.shadowRoot.querySelector('.sheet-close');
        this.sheetBody = this.shadowRoot.querySelector('.sheet-body');
        this.sheetBodySlot = this.shadowRoot.querySelector('.sheet-body slot');
        this.sheetFooter = this.shadowRoot.querySelector('.sheet-footer');
        this.sheetFooterSlot = this.shadowRoot.querySelector('.sheet-footer-slot');
        this.sheetBackdrop = this.shadowRoot.querySelector('.sheet-backdrop');
        this.referrerElement = null;

        this.openSheet = this.openSheet.bind(this);
        this.dragSheet = this.dragSheet.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.closeSheet = this.closeSheet.bind(this);
    }

    connectedCallback() {
        this.style.setProperty('--sheet-transition-duration', `${this.transitionDuration}ms`);
        this.sheetHandle.addEventListener('touchstart', this.onTouchStart);
        this.sheetHandle.addEventListener('touchmove', this.onTouchMove);
        this.sheetHandle.addEventListener('touchend', this.onTouchEnd);

        this.sheetBackdrop.addEventListener('touchend', this.handleBackdropClick);
        this.sheetBackdrop.addEventListener('click', this.handleBackdropClick);
        this.sheetCloseButton.addEventListener('click', this.closeSheet);

        if (this.hasAttribute('find-header-in-body')) {
            this.sheetBodySlot.addEventListener('slotchange', () => {
                const assignedNodes = this.sheetBodySlot.assignedNodes({ flatten: true });
                const sheetBodyContent = assignedNodes.find(node => node.nodeType === Node.ELEMENT_NODE);
                if (sheetBodyContent && !this.querySelector('[slot="sheet-header-text"]')) {
                    const headerText = webComponentsUtils.getFirstHeaderText(sheetBodyContent, true);
                    if (headerText) {
                        const header = document.createElement('span');
                        header.setAttribute('slot', 'sheet-header-text');
                        header.innerText = headerText;
                        header.classList.add('header-from-body');
                        this.prepend(header);
                    }
                }
            });
        }

        this.sheetFooterSlot.addEventListener('slotchange', () => {
            this.sheetFooter.classList.toggle('in-use', this.sheetFooterSlot.assignedNodes().length > 0);
        });

        this.setupExternalSheetOpeners();
        webComponentsUtils.closeSheetWithEscapeKey();
    }

    onTouchStart(e) {
        e.stopPropagation();
        if (e.cancelable) {
            e.preventDefault();
        }
        this.classList.add('dragging');
        this.initialY = Math.round(e.touches[0].clientY);
        this.containerTop = Math.round(this.sheetContainer.getBoundingClientRect().top);
        this.containerHeight = Math.round(this.sheetContainer.getBoundingClientRect().height);
        this.dragSheet(e.touches[0]);

        this.shadowRoot.querySelector('.sheet-handle span').style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
    }

    onTouchMove(e) {
        e.stopPropagation();
        this.dragSheet(e.touches[0]);
    }

    onTouchEnd(e) {
        e.stopPropagation();
        this.classList.remove('dragging');
        this.shadowRoot.querySelector('.sheet-handle span').style.removeProperty('background-color');

        const finalY = Math.round(e.changedTouches[0].clientY);
        const sheetHeight = this.sheetContainer.clientHeight;
        if (finalY - this.initialY > sheetHeight / 2) {
            this.closeSheet();
        } else {
            this.sheetContainer.style.transform = 'translateY(0)';
            this.sheetBackdrop.style.opacity = '1';
            setTimeout(() => {
                this.sheetContainer.style.removeProperty('transform');
            }, this.transitionDuration);
        }
    }

    dragSheet(touch) {
        const dragDistance = Math.round(touch.clientY) - this.containerTop;
        const percentOfSheetStillVisible = (1 - dragDistance / this.containerHeight).toFixed(2);

        // 30 is the height of the sheet handle. User needs to drag the sheet 30px before it starts moving
        if (dragDistance > 30) {
            this.sheetContainer.style.transform = `translateY(${dragDistance - 30}px)`;
        }

        this.sheetBackdrop.style.opacity = percentOfSheetStillVisible;
    }

    handleBackdropClick(e) {
        e.stopPropagation();
        e.preventDefault();
        this.closeSheet();
    }

    closeSheet() {
        this.dispatchEvent(new CustomEvent('sheet:before:close', { bubbles: true }));
        this.classList.add('animating');
        setTimeout(() => {
            this.classList.remove('animating');
            this.dispatchEvent(new CustomEvent('sheet:close', { bubbles: true }));
        }, this.transitionDuration);
        this.sheetContainer.style.removeProperty('transform');
        this.sheetBackdrop.style.removeProperty('opacity');
        this.classList.remove('open');
        document.body.classList.remove('overflow-hidden');
        this.referrerElement && this.referrerElement.focus();
    }

    openSheet(referrerElement) {
        this.dispatchEvent(new CustomEvent('sheet:before:open', { bubbles: true }));
        referrerElement && (this.referrerElement = referrerElement);
        this.classList.add('animating', 'open');
        setTimeout(() => {
            this.classList.remove('animating');
            this.sheetContainer.focus();
            this.dispatchEvent(new CustomEvent('sheet:open', { bubbles: true }));
        }, this.transitionDuration);
        document.body.classList.add('overflow-hidden');
    }

    setupExternalSheetOpeners() {
        const sheetId = this.getAttribute('id');
        if (sheetId) {
            const externalTriggerElements = document.querySelectorAll(`[data-sheet-target=${sheetId}]`);
            externalTriggerElements.forEach((element) => {
                element.addEventListener('click', (e) => {
                    this.openSheet(e.target);
                });
            });
        }
    }
}

module.exports = SheetComponent;
